import type { HairlineProps } from "@lucasmarkes/hairline/react";
import { CheckIcon, CopyIcon } from "lucide-react";
import { useState, type ComponentType } from "react";
import { useCopyToClipboard } from "~/hooks/useCopyToClipboard";
import { CONTACT_EMAIL } from "./helpMenu.logic";
import { Button } from "./ui/button";
import { HairlineArt } from "./ui/hairline-art";

export interface AppErrorScreenProps {
  /** Whether the failure was reported automatically (Sentry is on). */
  readonly notified: boolean;
  readonly onRetry: () => void;
  readonly supportEmail?: string;
  /** Overrides the generic title, for a failure with a known cause. */
  readonly title?: string;
  /** Overrides the line about whether anyone was told. */
  readonly message?: string;
  /** A figure above the title, for a failure with a known, benign cause; a crash gets none. */
  readonly art?: ComponentType<HairlineProps>;
}

/**
 * The full-page "something went wrong" screen: a title, a line saying
 * whether anyone was told, the support address with a copy button, and
 * one way back. It carries no stack trace — the report is what carries
 * that, and the person in front of it needs somewhere to write, not a
 * wall of frames.
 */
export function AppErrorScreen({
  notified,
  onRetry,
  supportEmail = CONTACT_EMAIL,
  title = "Something went wrong",
  message,
  art,
}: AppErrorScreenProps) {
  const [copyFailed, setCopyFailed] = useState(false);
  const { copyToClipboard, isCopied } = useCopyToClipboard({
    onCopy: () => setCopyFailed(false),
    onError: () => setCopyFailed(true),
  });
  // A failure within the hook's success window wins: one action, one verdict.
  const copied = isCopied && !copyFailed;
  const copyStatus = copyFailed
    ? "Could not copy the support email"
    : copied
      ? "Support email copied"
      : "";

  return (
    <div
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center bg-background px-6 py-16 text-foreground"
    >
      <div className="flex w-full max-w-3xl flex-col items-center gap-10 text-center">
        <div className="flex flex-col items-center gap-4">
          {art ? <HairlineArt figure={art} className="mb-2 w-64 sm:w-72" /> : null}
          <h1 className="text-2xl font-medium tracking-tight sm:text-[1.75rem]">
            {title}
          </h1>
          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
            {message ??
              (notified
                ? "We've been notified and are looking into it."
                : "Try again, and it should recover.")}
            <br />
            If this issue persists, please reach out to our support team.
          </p>
        </div>

        <div className="flex w-full items-center justify-between gap-4 border border-border px-4 py-4 sm:px-6">
          <a
            href={`mailto:${supportEmail}`}
            className="truncate text-base text-muted-foreground hover:text-foreground sm:text-lg"
          >
            {supportEmail}
          </a>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={copied ? "Support email copied" : "Copy support email"}
            onClick={() => {
              setCopyFailed(false);
              copyToClipboard(supportEmail, undefined);
            }}
          >
            {copied ? <CheckIcon aria-hidden /> : <CopyIcon aria-hidden />}
          </Button>
          {/* A label change is not announced; a live region is. */}
          <span className="sr-only" aria-live="polite">
            {copyStatus}
          </span>
        </div>

        <Button type="button" variant="outline" size="lg" onClick={onRetry}>
          Try again
        </Button>
      </div>
    </div>
  );
}
